<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                title="Team Task Management"
                :showAddNew="false"
            />
        </div>
        <div class="my-2 px-2">
          <div class="row">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Team Member</label>
              <AsyncSelect
                  placeholder="Team Member"
                  v-model="contactProfile"
                  :api-service="fetchContactProfiles"
                  :additional-query="{roles: 'employee', status: 'active'}"
                  label="name"
              />
            </div>
              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Activity Group</label>
                <AsyncSelect
                    placeholder="Select Activity"
                    v-model="activityGroup"
                    :additional-query="{type: 'group'}"
                    :api-service="fetchActivitySetupEndpoint"
                    @option:selected="onChangeActivityGroup"
                    label="name"
                />
              </div>

              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Organization Activity</label>
                <AsyncSelect
                    placeholder="Select Activity"
                    v-model="organizationActivity"
                    :api-service="fetchActivitySetupEndpoint"
                    label="name"
                    :additional-query="{ group_id: activityGroup && activityGroup.id }"
                    ref="organizationActivityDropdown"
                />
              </div>
              <div class="col-sm-2 col-md-2 d-flex align-items-end mt-2 action-btn-setter">
                <button
                    style="min-width: 64px;"
                    @click="onClickToGo"
                    class="btn btn-primary btn-block waves-effect waves-float waves-light"
                > Go
                </button>
              </div>
          </div>
        </div>

      <div class="col-12 px-2 mt-2 mb-4">
        <div class="card p-2" style="min-height: 200px">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <button class="btn btn-sm btn-outline-primary" @click="prevWeek">Previous Week</button>
            <strong>{{ weekRangeText }}</strong>
            <button class="btn btn-sm btn-outline-primary" @click="nextWeek">Next Week</button>
          </div>

          <div class="table-responsive">
            <table class="table table-bordered">
              <thead>
              <tr>
                <th>Team Member</th>
                <th v-for="(day, i) in weekDays" :key="i">
                  {{ formatDateToDay(day) }} <br> {{ formatDayToDateReadable(day) }}
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in tableItems" :key="i">
                <td>{{ item.name }}</td>
                <td v-for="(day, j) in weekDays" :key="j">
                  <div v-if="item.days[formatISODate(day)] && item.days[formatISODate(day)].length">
                      <div
                          v-for="(activity, k) in item.days[formatISODate(day)]"
                          @click="goToEdit(activity.id)"
                          :key="k"
                          style="padding: 4px; margin-bottom: 4px; border-radius: 4px;"
                          :style="{
                            backgroundColor: taskStatusColors[activity.status].bg || '#f9fafb',
                            color: taskStatusColors[activity.status].text || '#111827',
                          }"
                      >
                      <strong class="text-capitalize">{{ activity.type }}</strong> <br>
                      {{ activity.short_description }} <br>
                    </div>
                  </div>
                  <div v-else>
                    -
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
            <p v-if="tableItems.length === 0" class="text-center mt-5">No data</p>
          </div>
        </div>
      </div>

      <TaskEventScheduler
          v-if="store.state.isModalOpen"
          :startDate="startDate"
          :endDate="endDate"
          :editData="singleActivity"
          :taskStatus="taskStatus"
          :markAsDoneShow="markAsDoneShow"
          :isCompleted="isCompleted"
          @submitted="onEventSubmitted"
      />

     <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleActivity from "@/services/modules/procurement/activity";
import { computed } from 'vue';
import handleContact from '@/services/modules/contact'
import handleDropDowns from '@/services/modules/dropdowns'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import TaskEventScheduler from '@/components/molecule/company/admin/TaskEventScheduler.vue';
import { formatDayToDateReadable, formatDateToDay, formatISODate, getStartOfWeek, addDays, taskStatusColors } from '@/services/utils/global.js';

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const activityGroup = ref('');
const organizationActivity = ref('');
const client = ref('');
const contactProfile = ref('');
const loading = ref(false)
const tableItems = ref([]);
const singleActivity = ref(null);
const taskStatus = ref({});
const markAsDoneShow = ref(false);
const isCompleted = ref(false);
const organizationActivityDropdown = ref(null)
const token = computed(() => localStorage.getItem('token'));

const { fetchTeamTaskManagementList, fetchSingleTaskActivityLog } = handleActivity()
const { fetchContactProfiles } = handleContact()
const { fetchActivitySetupEndpoint } = handleDropDowns();

const onClickToGo = () => {
   getTaskManagementList();
}

const onChangeActivityGroup = () => {
  organizationActivity.value = null;
  organizationActivityDropdown.value.clear();
}

const onEventSubmitted = () => {
  getTaskManagementList();
};

const today = new Date();
const startDate = ref(getStartOfWeek(today));
const endDate = ref(addDays(getStartOfWeek(today), 6));

const weekDays = computed(() => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(startDate.value, i));
  }
  return days;
});

const weekRangeText = computed(() => {
  return `${formatDateToDay(startDate.value)} (${formatDayToDateReadable(startDate.value)}) - ${formatDateToDay(endDate.value)} (${formatDayToDateReadable(endDate.value)})`;
});

async function nextWeek() {
  startDate.value = addDays(startDate.value, 7);
  endDate.value = addDays(endDate.value, 7);
  await getTaskManagementList();
}

async function prevWeek() {
  startDate.value = addDays(startDate.value, -7);
  endDate.value = addDays(endDate.value, -7);
  await getTaskManagementList();
}

const goToEdit = async (activityId) =>  {
  if (!activityId) return;
  markAsDoneShow.value = false;
  isCompleted.value = false;
  loading.value = true;

  try {
    const res = await fetchSingleTaskActivityLog(activityId, getQuery());
    if (!res.status) return showError(res.message);
    let response = res.data;
    singleActivity.value = response;
    taskStatus.value = res.task_status;
    markAsDoneShow.value = true;
    if (['completed', 'approved'].includes(response.status)) {
      isCompleted.value = true;
    }
    store.state.isModalOpen = true;
  } finally {
    loading.value = false;
  }
}

const getTaskManagementList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchTeamTaskManagementList(query).then(res => {
        if (!res.status){
            return
        }
        tableItems.value = res.data;
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}&route_name=task-calendar`;
    query += '&start_date=' + formatISODate(startDate.value);
    query += '&end_date=' + formatISODate(endDate.value);
    if (organizationActivity.value) query += `&organization_activity=${organizationActivity.value.id}`;
    if (activityGroup.value) query += `&activity_group=${activityGroup.value.id}`;
    if (contactProfile.value) query += `&contact_profile_id=${contactProfile.value.id}`;
    return query;
}

onMounted(() => {
    Promise.all([
        getTaskManagementList(),
    ])
})

</script>
